import {selectorFamily, waitForAllSettled} from 'recoil';

import {TypeLocalNameEnum} from 'domain/usecases';
import {
	makeGetExamsAvailableTimes,
	makeGetExamsDates,
	makeGetExamsTypesLocation,
	makeGetHealthAvailableTimes,
	makeGetHealthDates,
	makeGetOdontoAvailableTimes,
	makeGetOdontoDates,
} from 'main/factories/usecases';
import {accountDataStateRefresher, refresherState} from 'presentation/pages/states';
import {sortAvailableTimes, sortByKey, sortDateStringArray} from 'presentation/utils';
import {formatGender} from 'presentation/utils/formatGender';
import {getCurrentAccountAdapter} from 'main/adapters/current-account-adapter';
import {EntryPointCodeEnum, FlowEnum, TELEMEDICINE_INFO} from '../../constants';
import {
	BookingAvailableTimesParams,
	BookingAvailableTimesResponse,
	BookingDateParams,
	BookingMultipleAvailableTimesParams,
	BookingMultipleAvailableTimesResponse,
	ExamAvailableTimesParams,
	ExamDateParams,
	HealthAvailableTimesParamsV2,
	HealthDateParams,
	OdontoAvailableTimesParams,
	OdontoDateParams,
} from './types';

const bookingHealthDatesState = selectorFamily<string[], HealthDateParams>({
	key: 'bookingHealthDates',
	get:
		({
			flowType,
			unitCode,
			specialtyCode,
			subSpecialtyCode,
			appointmentNumber,
			cityName,
			typeLocalName,
			ufCode,
			providerCode,
		}) =>
		async ({get}) => {
			get(refresherState);
			get(accountDataStateRefresher);

			const isTelehealth = flowType === FlowEnum.TELEHEALTH;

			const response = await makeGetHealthDates().get({
				specialty_code: String(specialtyCode),
				clinic_code: isTelehealth ? TELEMEDICINE_INFO.code : unitCode,
				entryPointCode: Number(EntryPointCodeEnum.V2),
				subspecialty_code: subSpecialtyCode ?? '',
				appointment_number: appointmentNumber,
				cityName,
				stateCode: ufCode,
				typeLocalName,
				providerCode,
			});

			if (!response) return [];

			return response.content;
		},
});

const bookingOdontoDatesState = selectorFamily<string[], OdontoDateParams>({
	key: 'bookingOdontoDates',
	get:
		({unitCode, specialtyCode, subSpecialtyCode, cityName, disability, districtName, ufCode}) =>
		async ({get}) => {
			get(refresherState);
			get(accountDataStateRefresher);

			const response = await makeGetOdontoDates().get({
				cdAtendimentoAcessoEspecial: disability,
				cdEspecialidade: Number(specialtyCode),
				cdPrestadorJuridico: String(unitCode),
				cdUf: ufCode.toUpperCase(),
				nmCidade: cityName.toUpperCase(),
				tipoPesquisa: '',
				cdSubEspecialidade: subSpecialtyCode ? Number(subSpecialtyCode) : 0,
				nmBairro: districtName?.toUpperCase(),
			});

			if (!response) return [];

			return response;
		},
});

const bookingExamDatesState = selectorFamily<string[], ExamDateParams>({
	key: 'bookingExamDates',
	get:
		({unitCode, specialtyCode, cityName, ufCode, typeLocalName}) =>
		async ({get}) => {
			get(refresherState);
			get(accountDataStateRefresher);

			const {
				beneficiary: {flSexoUsuarioC, idadeC},
			} = getCurrentAccountAdapter();

			const genderFormatted = formatGender(flSexoUsuarioC);

			const examTypes = await makeGetExamsTypesLocation().get({
				stateCode: ufCode,
				cityName,
				age: idadeC,
				gender: genderFormatted,
				typeLocalName: typeLocalName ?? TypeLocalNameEnum.CIDADE,
			});

			const examList = examTypes?.tiposExames;

			const procedureCode = examList.find(
				exam => exam.cdTipoExame === specialtyCode,
			)?.cdProcedimento;

			if (!procedureCode) {
				return [];
			}

			const response = await makeGetExamsDates().get({
				cdTipoExame: Number(specialtyCode),
				cdProcedimento: procedureCode,
				cdPrestadorJuridico: unitCode,
			});

			if (!response) {
				return [];
			}

			return response;
		},
});

export const bookingDatesState = selectorFamily<string[], BookingDateParams>({
	key: 'bookingDates',
	get:
		({
			cityName,
			disability,
			districtName,
			flowType,
			unitCode,
			specialtyCode,
			ufCode,
			subSpecialtyCode,
			appointmentNumber,
			typeLocalName,
			providerId,
		}) =>
		async ({get}) => {
			if (!cityName || !ufCode || !unitCode || !specialtyCode) return [];

			if (flowType === FlowEnum.ODONTO) {
				const dates = get(
					bookingOdontoDatesState({
						cityName: cityName.toUpperCase(),
						disability,
						districtName: districtName?.toUpperCase(),
						unitCode,
						specialtyCode,
						ufCode: ufCode.toUpperCase(),
						subSpecialtyCode,
					}),
				);
				return sortDateStringArray(dates);
			}

			if (flowType === FlowEnum.EXAM) {
				const dates = get(
					bookingExamDatesState({
						unitCode,
						specialtyCode,
						flowType,
						cityName: cityName.toUpperCase(),
						ufCode: ufCode.toUpperCase(),
						typeLocalName: typeLocalName ?? TypeLocalNameEnum.CIDADE,
					}),
				);
				return sortDateStringArray(dates);
			}

			const dates = get(
				bookingHealthDatesState({
					unitCode,
					specialtyCode,
					subSpecialtyCode,
					flowType,
					appointmentNumber,
					cityName: cityName.toUpperCase(),
					ufCode: ufCode.toUpperCase(),
					typeLocalName: typeLocalName ?? TypeLocalNameEnum.CIDADE,
					providerCode: providerId,
				}),
			);

			return sortDateStringArray(dates);
		},
});

const bookingHealthAvailableTimesState = selectorFamily<
	BookingAvailableTimesResponse[],
	HealthAvailableTimesParamsV2
>({
	key: 'bookingHealthAvailableTimes',
	get:
		({flowType, unitCode, specialtyCode, subSpecialtyCode, dateAt, shift, providerCode}) =>
		async ({get}) => {
			get(refresherState);
			get(accountDataStateRefresher);

			const isTelehealth = flowType === FlowEnum.TELEHEALTH;

			const response = await makeGetHealthAvailableTimes().get({
				date: dateAt,
				entryPointCode: Number(EntryPointCodeEnum.V2),
				shift_type: shift,
				specialty_code: String(specialtyCode),
				subspecialty_code: subSpecialtyCode ? String(subSpecialtyCode) : '',
				clinic_code: isTelehealth ? TELEMEDICINE_INFO.code : unitCode,
				providerCode,
			});

			if (!response || !response.content) {
				return [];
			}

			const {content} = response;
			const parsedResponse = content.providers.map(item => {
				const listAvailableTimes = item.availableTimes;
				const isPreviousProvider = item.isPreviousProvider;
				return {
					professionalName: item.name,
					professionalCode: String(item.code),
					isFavorite: item.isFavorite,
					availableTimes: listAvailableTimes,
					recommendations: [],
					isPreviousProvider,
				};
			});

			return parsedResponse;
		},
});

const bookingOdontoAvailableTimesState = selectorFamily<
	BookingAvailableTimesResponse[],
	OdontoAvailableTimesParams
>({
	key: 'bookingOdontoAvailableTimes',
	get:
		({
			unitCode,
			specialtyCode,
			subSpecialtyCode,
			cityName,
			disability,
			districtName,
			ufCode,
			dateAt,
			shift,
		}) =>
		async ({get}) => {
			get(refresherState);
			get(accountDataStateRefresher);

			const response = await makeGetOdontoAvailableTimes().get({
				cdAtendimentoAcessoEspecial: disability,
				cdEspecialidade: Number(specialtyCode),
				cdPrestadorJuridico: Number(unitCode),
				cdUf: ufCode.toUpperCase(),
				nmCidade: cityName.toUpperCase(),
				cdSubEspecialidade: subSpecialtyCode ? Number(subSpecialtyCode) : 0,
				nmBairro: districtName ? districtName.toUpperCase() : undefined,
				dataConsulta: dateAt,
				filtroTurno: shift,
			});

			if (!response) return [];

			const {horariosDisponiveis} = response;
			const parsedResponse = horariosDisponiveis.map(item => ({
				professionalName: item.nmPrestadorFisico,
				professionalCode: String(item.cdPrestadorFisico),
				availableTimes: sortAvailableTimes(item.horariosDisponiveis),
				recommendations: item.dsRecomendacoes,
			}));
			const sortedAvailableTimesByProviderName = sortByKey(
				parsedResponse,
				'professionalName',
			);

			return sortedAvailableTimesByProviderName;
		},
});

const bookingExamAvailableTimesState = selectorFamily<
	BookingAvailableTimesResponse[],
	ExamAvailableTimesParams
>({
	key: 'bookingExamAvailable',
	get:
		({unitCode, specialtyCode, dateAt, shift}) =>
		async ({get}) => {
			get(refresherState);
			get(accountDataStateRefresher);

			const response = await makeGetExamsAvailableTimes().get({
				dtData: dateAt,
				filtroTurno: shift,
				cdTipoExame: String(specialtyCode),
				cdPrestadorJuridico: String(unitCode),
			});

			if (!response) return [];

			const {horariosDisponiveis} = response;
			const parsedResponse = horariosDisponiveis.map(item => ({
				professionalName: item.nmPrestadorFisico,
				professionalCode: String(item.cdPrestadorFisico),
				availableTimes: sortAvailableTimes(item.horariosDisponiveis),
				recommendations: item.dsRecomendacoes,
			}));
			const sortedAvailableTimesByProviderName = sortByKey(
				parsedResponse,
				'professionalName',
			);

			return sortedAvailableTimesByProviderName;
		},
});

export const bookingAvailableTimesState = selectorFamily<
	BookingAvailableTimesResponse[],
	BookingAvailableTimesParams
>({
	key: 'bookingAvailableTimes',
	get:
		({
			dateAt,
			shift,
			cityName,
			ufCode,
			disability,
			districtName,
			flowType,
			unitCode,
			specialtyCode,
			subSpecialtyCode,
			providerId,
		}) =>
		async ({get}) => {
			if (!cityName || !ufCode || !unitCode || !specialtyCode) return [];

			if (flowType === FlowEnum.ODONTO) {
				const times = get(
					bookingOdontoAvailableTimesState({
						dateAt,
						shift,
						cityName: cityName.toUpperCase(),
						disability,
						districtName: districtName ? districtName.toUpperCase() : undefined,
						unitCode,
						specialtyCode,
						ufCode: ufCode.toUpperCase(),
						subSpecialtyCode,
					}),
				);
				return times;
			}

			if (flowType === FlowEnum.EXAM) {
				const times = get(
					bookingExamAvailableTimesState({
						unitCode,
						specialtyCode,
						dateAt,
						shift,
					}),
				);
				return times;
			}

			const times = get(
				bookingHealthAvailableTimesState({
					unitCode,
					specialtyCode,
					flowType,
					subSpecialtyCode,
					dateAt,
					shift,
					providerCode: providerId,
				}),
			);
			return times;
		},
});

export const bookingMultipleAvailableTimesState = selectorFamily<
	BookingMultipleAvailableTimesResponse[],
	BookingMultipleAvailableTimesParams
>({
	key: 'bookingMultipleAvailableTimes',
	get:
		({
			dates,
			shift,
			cityName,
			ufCode,
			disability,
			districtName,
			flowType,
			unitCode,
			specialtyCode,
			subSpecialtyCode,
			providerId,
		}) =>
		async ({get}) => {
			if (!cityName || !ufCode || !unitCode || !specialtyCode || !dates) return [];

			const firstThreeDates = dates.slice(0, 3);

			const availableTimes = get(
				waitForAllSettled(
					firstThreeDates.map(date =>
						bookingAvailableTimesState({
							cityName: cityName.toUpperCase(),
							dateAt: date,
							disability,
							districtName: districtName ? districtName.toUpperCase() : undefined,
							flowType,
							shift,
							specialtyCode,
							ufCode: ufCode.toUpperCase(),
							subSpecialtyCode,
							unitCode,
							providerId,
						}),
					),
				),
			);

			const formattedAvailableTimes = availableTimes.map((item, index) => ({
				date: firstThreeDates[index],
				times: item.contents,
			}));

			return formattedAvailableTimes;
		},
});
